import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import InteriorResidents from './InteriorResidents';
import ListeningSeats from './ListeningSeats';
import EnsembleDirector from './EnsembleDirector';
import { useFrame } from '@react-three/fiber';
import { Text, Html, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import MeshInterior from './MeshInterior';
import SplatWorld from './SplatWorld';
import VideoPanel from './VideoPanel';
import RoomCollider from './RoomCollider';
import PlaceableBadges from './PlaceableBadges';
import useRoomPlaceables from '../hooks/useRoomPlaceables';
import RecordStore from './RecordStore';
import NewsTv from './NewsTv';
import AnimatedResident from './AnimatedResident';
import { beatPulse } from '../utils/worldBeat';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, authUser, WORLD_PERFORMER, IS_MOBILE, VIDEO_OFF } from '../config';
import RecordPlaques from './RecordPlaques';

// A 2.5D resident — stylized figure standing at the mixing board.
// In the full pipeline these are Gemini-generated sprite characters (v0.2 §7);
// the slice renders a placeholder body with the resident's real name.
function Resident({ name, position, beatPhase, scale = 1 }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.y = Math.sin(beatPhase * Math.PI * 2) * 0.06;
    }
  });
  return (
    <group position={position} scale={scale}>
      <mesh ref={ref} castShadow>
        <capsuleGeometry args={[0.32, 1.0, 6, 16]} />
        <meshStandardMaterial color="#2e2a3a" roughness={0.7} metalness={0.2} />
      </mesh>
      <Text position={[0, 1.5, 0]} fontSize={0.28} color="#ffd27d" anchorX="center">
        {name}
      </Text>
    </group>
  );
}

// Shared plumbing for a Marble splat room: loads the room's manifest, tracks
// whether the splat is live (so the procedural fallback shell can hide), and
// pushes a manifest-shipped designed spawn into the camera rig when present.
function useSplatRoom(roomId) {
  const [splatReady, setSplatReady] = useState(false);
  const [manifest, setManifest] = useState(null);
  // The splat's as-fitted {position, scale} — the frame a solid twin
  // (RoomCollider) must be mounted in to line up with what you see.
  const [fit, setFit] = useState(null);
  const setSpawnOverride = useWorldStore((s) => s.setSpawnOverride);
  const room = ROOMS[roomId];
  const splat = (
    <SplatWorld
      manifestUrl={room.manifest}
      onLoaded={(m, fitted) => {
        setSplatReady(true);
        setManifest(m);
        setFit(fitted || null);
        if (m?.spawn?.pos && m?.spawn?.look) setSpawnOverride(m.spawn);
      }}
      onError={() => setSplatReady(false)}
    />
  );
  return { splat, splatReady, manifest, fit };
}

// Mixing board — the diegetic "make music" surface (song generation = the board).
function MixingBoard({ beatPhase, energy }) {
  const glow = useRef();
  useFrame(() => {
    if (glow.current) {
      glow.current.material.emissiveIntensity = 0.8 + Math.abs(Math.sin(beatPhase * Math.PI * 2)) * (0.6 + energy);
    }
  });
  return (
    <group position={[0, 0, -3]}>
      <mesh castShadow position={[0, 0.9, 0]}>
        <boxGeometry args={[4.4, 0.35, 1.6]} />
        <meshStandardMaterial color="#1c1c22" roughness={0.6} metalness={0.4} />
      </mesh>
      <mesh ref={glow} position={[0, 1.1, 0]} rotation={[-Math.PI / 6, 0, 0]}>
        <planeGeometry args={[4.0, 1.1]} />
        <meshStandardMaterial color="#0a0a12" emissive="#ffb25e" emissiveIntensity={1.0} />
      </mesh>
      {/* Faders */}
      {Array.from({ length: 12 }).map((_, i) => (
        <mesh key={i} position={[-1.8 + i * 0.33, 1.08, 0.1]}>
          <boxGeometry args={[0.06, 0.06, 0.5]} />
          <meshStandardMaterial color="#d9d9e0" emissive="#ff5e78" emissiveIntensity={0.4} />
        </mesh>
      ))}
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[4.4, 0.9, 1.4]} />
        <meshStandardMaterial color="#26262e" roughness={0.8} />
      </mesh>
    </group>
  );
}

// A user-scanned room lifted through the RoomScan → Gemini → Marble pipeline
// ("Your Room Is The Studio"). Pure splat — the scan IS the room, so there's
// no procedural furniture and nothing but a soft light while it streams in.
//
// Plus the two things that make it a MEET venue:
//  · a DORMANT video panel — no recorded cameo, so VideoPanel renders nothing
//    until a live meet sets liveVideoEl (MeetDock), at which point the artist
//    appears on the wall of their own scanned studio. Tune with ?panel=edit.
//  · the room's SOLID twin — the Marble collider mesh mounted in the splat's
//    exact fitted frame (RoomCollider): invisible geometry to raycast, debug
//    with ?collider=1, floor-feed with ?walk=1.
function ScannedRoom({ roomId = 'room-studio' }) {
  const { splat, splatReady, manifest, fit } = useSplatRoom(roomId);
  // Rows-over-seeds (⚙ → Room placeables): a saved video_panel row wins over
  // the rooms.js default. While still seed-backed (string id) the old
  // ?panel=edit localStorage tuner keeps working; once a row exists it is the
  // arrangement and localStorage stands down. Baked MOMENT scenes
  // (moment-<postId>) have no seeds — rows only, so any moment becomes
  // screen-capable the moment a panel row is saved into it.
  const panels = useRoomPlaceables(roomId, 'video_panel');
  const vp = panels[0];
  // The room's solid twin: moment manifests carry their own (proxied)
  // collider URL; the original scanned studio keeps its deployed copy.
  const colliderUrl = manifest?.collider_mesh_url
    || (roomId === 'room-studio' ? '/worlds/room_studio_collider.glb' : null);
  return (
    <group>
      {splat}
      {vp && (
        <VideoPanel
          position={vp.pos}
          rotationY={vp.rotY}
          height={vp.size}
          billboard={vp.data?.billboard ?? false}
          storageKey={typeof vp.object_id === 'string' ? `videoPanel:${roomId}` : null}
        />
      )}
      {splatReady && fit && colliderUrl && (
        <Suspense fallback={null}>
          <RoomCollider url={colliderUrl} fit={fit} />
        </Suspense>
      )}
      {!splatReady && <ambientLight intensity={0.5} />}
    </group>
  );
}

// The studio interior: an artist is already here, mid-session.
// Presence (v0.2 §6): other listeners in this venue appear as felt figures.
// Whose face the studio ships with. The splat manifest's cameo was recorded of
// this artist, so they — and only they — can inherit it when they have no
// producer clip of their own.
const STUDIO_RESIDENT_AVATAR = ROOMS.studio?.liveChat?.avatarId ?? null;

function Studio({ beatPhase, energy }) {
  const { splat, splatReady, manifest } = useSplatRoom('studio');
  const producer = useWorldStore((s) => s.producer);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  // THE ENGINEER ON THE PANEL IS WHOEVER YOU PICKED (Khari, 2026-08-04: "we can
  // just swap the assets and the ID... no model, but video").
  //
  // The producer's own clip wins. Rebel inherits the room's manifest cameo
  // because that recording IS him. Anybody else without a clip gets NO cameo —
  // a dormant panel, exactly as it sits for a meet — because playing Rebel's
  // video while Devvin Keys is talking to you is worse than an empty screen.
  const mine = producer && String(producer.avatarId) === String(myAvatarId) ? producer : null;
  const cameo = (() => {
    if (!mine) return manifest?.video;                 // nobody picked: the room's own resident
    if (mine.video?.loop || mine.video?.greeting) return mine.video;
    // `producerAvatarId`, NOT `avatarId` — a stored pick puts the PLAYER's
    // avatar in `avatarId` (that is how SceneDock scopes whose pick it is), so
    // the producer's own id is carried separately.
    if (STUDIO_RESIDENT_AVATAR && Number(mine.producerAvatarId) === STUDIO_RESIDENT_AVATAR) {
      return manifest?.video;
    }
    console.log(`[studio] ${mine.name} has no cameo clip yet — panel stays dormant`);
    return null;
  })();
  // Rows-over-seeds for everything hung in this room (⚙ → Room placeables):
  // saved world_objects rows win; the rooms.js arrays are only the seeds a
  // fresh room ships with. While seed-backed the old per-type localStorage
  // tuners keep working; a saved row IS the arrangement.
  const panels = useRoomPlaceables('studio', 'video_panel');
  const vp = panels[0];
  const plaqueRows = useRoomPlaceables('studio', 'plaque');
  const resultRows = useRoomPlaceables('studio', 'result_card');
  const rowsBacked = (rows) => rows.length > 0 && typeof rows[0].object_id === 'number';
  const asPlaques = (rows) => rows.map((o) => ({ pos: o.pos, rotY: o.rotY, size: o.size }));
  return (
    <group>
      {/* The video cameo — the resident avatar in the room with you (greeting
          once, then a silent working loop). Background baked from the same
          pano as the splat, so it composites. */}
      {cameo && vp && (
        <VideoPanel
          greetingUrl={cameo.greeting}
          loopUrl={cameo.loop}
          position={vp.pos}
          rotationY={vp.rotY}
          height={vp.size}
          billboard={vp.data?.billboard ?? false}
          storageKey={typeof vp.object_id === 'string' ? 'videoPanel:studio' : null}
        />
      )}
      {/* THE WALL — released records only. A frame here is earned. */}
      {plaqueRows.length > 0 && (
        <RecordPlaques
          plaques={asPlaques(plaqueRows)}
          group="wall"
          source="released"
          storageKey={rowsBacked(plaqueRows) ? null : 'plaques:studio'}
        />
      )}
      {/* RESULT CARDS — what just came out of the room, lying where they were
          put and squaring up to you when you look at them. */}
      {resultRows.length > 0 && (
        <RecordPlaques
          plaques={asPlaques(resultRows)}
          group="results"
          source="undecided"
          storageKey={rowsBacked(resultRows) ? null : 'results:studio'}
        />
      )}

      {/* Photoreal Marble splat interior. Renders behind the procedural
          overlay props; when the splat loads we hide the placeholder room shell. */}
      {splat}

      {/* Placeholder room shell — only until the splat arrives (and as a
          fallback if the splat 404s), so the venue is never an empty void. */}
      {!splatReady && (
      <>
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[16, 12]} />
        <meshStandardMaterial color="#241a16" roughness={1} />
      </mesh>
      {[
        { p: [0, 2.5, -6], r: [0, 0, 0], s: [16, 5] },
        { p: [-8, 2.5, 0], r: [0, Math.PI / 2, 0], s: [12, 5] },
        { p: [8, 2.5, 0], r: [0, -Math.PI / 2, 0], s: [12, 5] }
      ].map((w, i) => (
        <mesh key={i} position={w.p} rotation={w.r}>
          <planeGeometry args={w.s} />
          <meshStandardMaterial color="#2c1f1a" roughness={1} />
        </mesh>
      ))}

      {/* Acoustic panels */}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh key={i} position={[-7 + i * 2, 3, -5.9]}>
          <boxGeometry args={[1, 1.4, 0.1]} />
          <meshStandardMaterial color={i % 2 ? '#3a2a24' : '#4a352c'} roughness={1} />
        </mesh>
      ))}
      </>
      )}

      {/* Overlay props were sized for the 16m placeholder shell; the fitted
          Marble room is metric (~4–6m), so compact them when the splat is live.
          Positions are eyeballed against the draft studio — tune in-scene. */}
      <group scale={splatReady ? 0.55 : 1} position={splatReady ? [0, 0, 0.6] : [0, 0, 0]}>
        <MixingBoard beatPhase={beatPhase} energy={energy} />

        {/* The resident placeholder — retired once the video cameo is live */}
        {!cameo && <Resident name="Rebel Rhythm" position={[0, 0.85, -4.2]} beatPhase={beatPhase} />}

        {/* Other people now render globally via <Peers/> at their real positions. */}
      </group>

      {/* Warm practicals */}
      <pointLight position={[0, 4, -2]} color="#ffb25e" intensity={20} distance={14} castShadow />
      <pointLight position={[-5, 3, 3]} color="#ff5e78" intensity={6} distance={10} />
    </group>
  );
}

// The Listening Room: the cozy social space — this room sells *presence*.
// Streams the district station (already playing via the broadcast layer);
// interactive prop = the glowing turntable console. Fallback shell is metric
// (~7×6m) so overlay props hold their positions when the splat arrives.
function ListeningRoom({ beatPhase, energy }) {
  const { splat, splatReady } = useSplatRoom('listening-room');
  const platter = useRef();
  useFrame((_, dt) => {
    if (platter.current) platter.current.rotation.y += dt * 3.5; // 33⅓-ish, sped for read
  });
  return (
    <group>
      {splat}

      {!splatReady && (
        <>
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[7, 6]} />
            <meshStandardMaterial color="#26181d" roughness={1} />
          </mesh>
          {[
            { p: [0, 1.5, -3], r: [0, 0, 0], s: [7, 3] },
            { p: [-3.5, 1.5, 0], r: [0, Math.PI / 2, 0], s: [6, 3] },
            { p: [3.5, 1.5, 0], r: [0, -Math.PI / 2, 0], s: [6, 3] }
          ].map((w, i) => (
            <mesh key={i} position={w.p} rotation={w.r}>
              <planeGeometry args={w.s} />
              <meshStandardMaterial color="#33212a" roughness={1} />
            </mesh>
          ))}
          {/* Record shelf + couch, blocked in */}
          <mesh castShadow position={[-2.4, 0.9, -2.7]}>
            <boxGeometry args={[1.8, 1.8, 0.4]} />
            <meshStandardMaterial color="#4a3040" roughness={0.9} />
          </mesh>
          <mesh castShadow position={[1.6, 0.4, 1.4]}>
            <boxGeometry args={[2.4, 0.8, 1]} />
            <meshStandardMaterial color="#5a3a44" roughness={1} />
          </mesh>
          {/* Album art on the walls — brand-ingredient stand-ins */}
          {[-1.5, 0, 1.5].map((x, i) => (
            <mesh key={i} position={[x, 1.9, -2.95]}>
              <planeGeometry args={[1, 1]} />
              <meshStandardMaterial color={['#ff5e78', '#ffb25e', '#7dd3ff'][i]} emissive={['#ff5e78', '#ffb25e', '#7dd3ff'][i]} emissiveIntensity={0.25} />
            </mesh>
          ))}

          {/* Turntable console — a blocked-in stand-in, NOT furniture.
              MOVED INSIDE THE !splatReady BLOCK (Khari, 2026-08-07: "we had
              started with simple 3D models in rooms.. we evolved to the splat,
              but it looks like it left the models in place"). The walls, floor
              and couch above were always gated; this one was not, so it kept
              rendering after the scan arrived and stood a black box with a red
              platter straight through the real armchair.
              A scanned room already HAS a turntable in it — it was photographed
              with one. */}
          <group position={[0, 0, -1.8]}>
            <mesh castShadow position={[0, 0.5, 0]}>
              <boxGeometry args={[1.4, 1.0, 0.7]} />
              <meshStandardMaterial color="#1c1c22" roughness={0.6} metalness={0.4} />
            </mesh>
            <mesh ref={platter} position={[0, 1.04, 0]}>
              <cylinderGeometry args={[0.42, 0.42, 0.04, 32]} />
              <meshStandardMaterial color="#0c0c10" roughness={0.4} metalness={0.3} emissive="#ff5e78" emissiveIntensity={0.15 + energy * 0.3} />
            </mesh>
          </group>
        </>
      )}

      {/* Other people now render globally via <Peers/> at their real positions. */}

      <pointLight position={[0, 2.4, 0]} color="#ff9a3c" intensity={10} distance={9} castShadow />
      <pointLight position={[-2.4, 1.6, -2]} color="#ff5e78" intensity={4} distance={6} />
    </group>
  );
}

// A spotlight that actually aims where told — three.js spotlights aim at a
// target Object3D that must live in the scene graph, so `target-position`
// alone silently aims at the origin.
function AimedSpot({ position, aim, ...props }) {
  const target = useMemo(() => new THREE.Object3D(), []);
  return (
    <>
      <primitive object={target} position={aim} />
      <spotLight position={position} target={target} {...props} />
    </>
  );
}

// Audio-reactive particle wash over the stage — splat light is baked, so all
// "show reactivity" lives in overlays like this (Demo Build Plan: hall recipe).
function StageParticles({ energy, accent, centerZ = -10, spreadX = 14, spreadZ = 8 }) {
  const ref = useRef();
  const { positions, seeds } = useMemo(() => {
    const n = 350;
    const positions = new Float32Array(n * 3);
    const seeds = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      positions[i * 3] = (Math.random() - 0.5) * spreadX;         // across the stage
      positions[i * 3 + 1] = 0.5 + Math.random() * 6;             // deck → rig height
      positions[i * 3 + 2] = centerZ + (Math.random() - 0.5) * spreadZ; // over the stage
      seeds[i] = Math.random();
    }
    return { positions, seeds };
  }, [centerZ, spreadX, spreadZ]);
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const t = clock.getElapsedTime();
    const beat = beatPulse(); // tempo-correct swell (octave-folded, playhead-anchored)
    ref.current.material.opacity = 0.25 + beat * (0.3 + energy * 0.4);
    ref.current.material.size = 0.06 + beat * energy * 0.1;
    const pos = ref.current.geometry.attributes.position;
    for (let i = 0; i < seeds.length; i++) {
      pos.array[i * 3 + 1] += Math.sin(t * (0.6 + seeds[i]) + seeds[i] * 7) * 0.003;
    }
    pos.needsUpdate = true;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color={accent} size={0.08} transparent opacity={0.4} depthWrite={false} blending={THREE.AdditiveBlending} />
    </points>
  );
}

// KnotSculpture — the spinning torus-knot particle sculpture hanging over the
// stage (ported from the classic three.js "particles from a TorusKnot" demo).
// The old r68 API is gone in three 0.180, so: ParticleSystem → Points,
// ParticleBasicMaterial → PointsMaterial, and TorusKnotGeometry's segment args
// SWAPPED meaning (modern order is radius, tube, tubularSegments-along-the-curve,
// radialSegments-around-the-tube) — the demo's 600/12 map to that order as-is.
// heightScale was dropped from the geometry entirely; we shape with p/q only.
//
// Sized to ENGULF the hall rather than sit in it: centred on the room and scaled
// past every wall, so you stand inside the knot and its strands read as orbs
// swinging around you. Only the arcs inside the room are visible — the walls
// depth-occlude the rest — so orbs sweep in through one wall and out the other.
//
// Tune live (no rebuild): ?knot=0 (off) · ?knotfill (× the room, default 1.6) ·
// ?knotspan (hard span in m, overrides fill) · ?knotx / ?knoty / ?knotz
// (re-centre) · ?knotspin (rad/sec) · ?knotsize (orb size, m) · ?knotrings
// (orbs along the curve) · ?knotbeat (0–1 beat reactivity).
// fitW/fitH/fitD are passed as plain numbers, not a tuple — an inline array prop
// is a new identity every render and would rebuild the geometry every frame.
function KnotSculpture({ position, fitW, fitH, fitD, fill = 1.6, energy = 0.5 }) {
  const ref = useRef();

  // Radial-gradient sprite (white core → cyan → deep blue → black). Black reads
  // as transparent under additive blending, so no alpha channel is needed.
  const sprite = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 16; canvas.height = 16;
    const ctx = canvas.getContext('2d');
    const g = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.2, 'rgba(0,255,255,1)');
    g.addColorStop(0.4, 'rgba(0,0,64,1)');
    g.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 16, 16);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, []);
  useEffect(() => () => sprite.dispose(), [sprite]);

  // Authored at the demo's dimensions (176.4 × 176.4 × 96.4 units — X and Y are
  // equal, Z is the knot's THIN axis at 0.55×), then baked to metres. Scale lives
  // in the geometry, not the object, because three computes gl_PointSize from the
  // material in WORLD units and ignores object scale — a scaled group would leave
  // the orbs at their old pixel size.
  //
  // Fit is per-axis and takes the LARGEST ratio, so every axis clears the room —
  // fitting by width alone left the thin Z axis shorter than the hall is deep,
  // which is exactly how you end up seeing the knot's edges from the floor.
  const span = useMemo(() => {
    const hard = qp('knotspan', 0);
    if (hard > 0) return hard;
    const cover = Math.max(fitW / 176.4, fitH / 176.4, fitD / 96.37);
    return 176.4 * cover * fill;
  }, [fitW, fitH, fitD, fill]);

  const geometry = useMemo(() => {
    // Rings along the curve. Fewer on phones: at room scale each orb can cover a
    // lot of screen, and these are additive, so fill rate is the real ceiling.
    const rings = qp('knotrings', IS_MOBILE ? 260 : 600);
    const src = new THREE.TorusKnotGeometry(40, 28.2, Math.round(rings), 12, 5, 4);
    // Drop the index: THREE.Points draws through it, which would re-draw every
    // shared vertex ~6× (43k additive orbs instead of 7.8k) for no visual gain.
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', src.getAttribute('position').clone());
    src.dispose();
    g.computeBoundingBox();
    const size = new THREE.Vector3();
    g.boundingBox.getSize(size);
    const k = span / size.x;
    g.scale(k, k, k);
    return g;
  }, [span]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  // Orb size rides the span — at hall scale a 7cm point is invisible dust, and
  // the same number relative to the knot keeps the look constant as it's tuned.
  const baseSize = qp('knotsize', span * (IS_MOBILE ? 0.005 : 0.004));
  const spin = qp('knotspin', 0.6);         // demo ran 0.01 rad/frame @60fps
  const reactivity = qp('knotbeat', 1);
  useFrame((_, dt) => {
    if (!ref.current) return;
    ref.current.rotation.y += dt * spin;
    // Rides the same tempo-correct swell as every other reactive visual.
    const beat = beatPulse() * reactivity;
    ref.current.material.size = baseSize * (1 + beat * (0.35 + energy * 0.5));
    ref.current.material.opacity = 0.72 + beat * 0.28;
  });

  return (
    <points ref={ref} position={position} frustumCulled={false}>
      <primitive object={geometry} attach="geometry" />
      <pointsMaterial
        color="#ffffff"
        size={baseSize}
        map={sprite}
        transparent
        opacity={0.8}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}

// The Concert Hall: the showstopper. Structure comes from the Chisel blockout
// (scripts/build_hall_blockout.mjs mirrors this shell); performer = hologram
// placeholder until the alpha-matted avatar video lands; crowd = presence.
// The baked virtual stage (GLB) that drops into the concert hall. Authored huge
// (~195 units wide), so we fit it to a target WIDTH in metres — oversized on
// purpose so a ~1.8m performer reads small on it. Grounded at y=0, centred on x.
// Dial it in live (no rebuild): ?stagew (width) · ?stagez / ?stagex / ?stagey
// (position) · ?stagerot (yaw, use ~3.14 if the screen faces away).
function StageModel() {
  const { scene } = useGLTF('/models/virtual_stage.glb');
  const model = useMemo(() => scene.clone(true), [scene]);
  const q = (k, d) => {
    const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
    return Number.isFinite(v) ? v : d;
  };
  const { scale, pos, rotY } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3(); box.getSize(size);
    const s = q('stagew', 48) / (size.x || 1);          // oversize by target width (m)
    const cx = (box.min.x + box.max.x) / 2;
    return {
      scale: s,
      // stagey lifts the stage a hair ABOVE the scene floor (y=0) so the stage's
      // own base doesn't z-fight the ground — the scene floor stays the real,
      // continuous floor; the stage just rests on it.
      pos: [-cx * s + q('stagex', 0), -box.min.y * s + q('stagey', 0.12), q('stagez', -22)],
      rotY: q('stagerot', 0),
    };
  }, [model]);
  // dispose={null}: shares geometry/material with the useGLTF cache (see CityModel).
  return <primitive object={model} scale={scale} position={pos} rotation={[0, rotY, 0]} dispose={null} />;
}
useGLTF.preload('/models/virtual_stage.glb');

// The rigged roster residents on the concert hall stage, each at ITS OWN tuned
// height. Tune live: ?avh (height) · ?avgap (spacing) · ?avz (depth) · ?avy
// (raise onto the stage) · ?avrot (yaw).
function ConcertHallAvatars() {
  const [roster, setRoster] = useState([]);
  useEffect(() => {
    fetch('/models/avatars/roster.json', { cache: 'no-store' })
      // `test: true` entries are pipeline A/B bodies, not residents — the street
      // is where they get compared, so keep them out of the lineup.
      .then((r) => r.json()).then((d) => setRoster((d.npcs || []).filter((n) => !n.test))).catch(() => setRoster([]));
  }, []);
  // Default the lineup onto the stage (tracks ?stagew/?stagez) and SCATTER them
  // over the deck with a deterministic hash — a troupe, not a straight row, each
  // facing a slightly different way. Tune: ?avh ?avy ?avrot ?avz ?avspanx ?avspanz.
  const sW = qp('stagew', 48), sZ = qp('stagez', -22), sc = sW / STAGE_BOX.w;
  const stageCenterZ = ((STAGE_BOX.zMin + STAGE_BOX.zMax) / 2) * sc + sZ;
  // ?avh is an OVERRIDE, not a default. It began as a scale check — one shared
  // 1.8m so the stage could be sized against a known human — but once every
  // resident had a TUNED roster height (0.6–1.2 in world units) that constant
  // silently rendered the whole lineup 1.5–3× too large, and re-tuning on the
  // street never appeared to take. 0 = "unset" so each keeps its own size.
  const H = qp('avh', 0), Y = qp('avy', 0), ROT = qp('avrot', 0);
  const Z = qp('avz', stageCenterZ);
  const SPANX = qp('avspanx', Math.min(sW * 0.7, 34)), SPANZ = qp('avspanz', 12);
  const hash = (n) => { const x = Math.sin(n * 127.1) * 43758.5453; return x - Math.floor(x); };
  if (!roster.length) return null;
  return (
    <Suspense fallback={null}>
      {roster.map((n, i) => (
        <AnimatedResident
          key={n.slug || n.avatarId}
          meshUrl={n.rigged}
          clipUrls={[n.walk, n.idle].filter(Boolean)}
          play="idle"
          height={H || n.height || 1.8}
          position={[(hash(i * 2 + 1) - 0.5) * SPANX, Y, Z + (hash(i * 2 + 7) - 0.5) * SPANZ]}
          rotationY={ROT + (hash(i * 3 + 5) - 0.5) * 1.4}
        />
      ))}
    </Suspense>
  );
}

// Baked stage GLB extents (measured): width 195.42, height 16.11, depth
// spanning z −65.77…51.88. Used to fit the stage AND size the room around it.
const STAGE_BOX = { w: 195.42, h: 16.11, zMin: -65.77, zMax: 51.88 };
const qp = (k, d) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) ? v : d;
};

// Proxy GCS media through the API (WebGL VideoTextures are CORS-gated).
function proxiedMedia(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* passthrough */ }
  return url;
}

// PerformerImage — the COMPOSITING test: a transparent-PNG performer (a pose
// cutout) rendered with real alpha on the stage, so the figure reads against
// the hall with no rectangle. This is the still-image starting point; animating
// it to a loop comes later. Source: ?perfimg=<url> (a transparent PNG). Until
// one is set, a cyan hologram stands in. alphaTest gives a clean cutout edge.
function PerformerImage({ position, height = 3, img = null }) {
  const q = (k, d) => { const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k)); return Number.isFinite(v) ? v : d; };
  // ?perfimg overrides the room's shipped default (rooms.js concert-hall.performer.img).
  const url = useMemo(() => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perfimg')) || img || null, [img]);
  // Height + feet come from the live store (PerformerPanel), seeded from URL.
  const perf = useWorldStore((s) => s.perfTune);
  const H = perf.h;                                               // performer height (m)
  const pos = [position[0] + q('perfx', 0), perf.y, position[2] + q('perfz', 0)];
  const [tex, setTex] = useState(null);
  const [aspect, setAspect] = useState(0.5);

  useEffect(() => {
    if (!url) return undefined;
    let dead = false;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(
      proxiedMedia(url),
      (t) => { if (dead) return; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; if (t.image?.width) setAspect(t.image.width / t.image.height); setTex(t); },
      undefined,
      () => console.warn('[performer] image failed:', url),
    );
    return () => { dead = true; };
  }, [url]);

  if (!tex) {
    return (
      <mesh position={pos}>
        <planeGeometry args={[H * 0.55, H]} />
        <meshBasicMaterial color="#7dd3ff" transparent opacity={0.45} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    );
  }
  return (
    <mesh position={pos}>
      <planeGeometry args={[H * aspect, H]} />
      {/* transparent + alphaTest: clean cutout edge, correct depth, shows the
          studio-lit figure as-authored (toneMapped off). */}
      <meshBasicMaterial map={tex} transparent alphaTest={0.5} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}

// PerformerVideo — a looping performance clip whose BLACK background is keyed
// out in-shader (luma threshold), so the figure composites against the hall.
// Source ?perfvideo=<url>; muted (the station carries audio). Shares perfTune
// height/feet with the still. Tune the cut with ?keylo / ?keyhi.
function PerformerVideo({ position, video = null }) {
  const q = (k, d) => { const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k)); return Number.isFinite(v) ? v : d; };
  // ?perfvideo overrides the backend default (CONCERT_HALL_1_PERFORMER via config).
  const url = useMemo(() => (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perfvideo')) || video || null, [video]);
  const perf = useWorldStore((s) => s.perfTune);
  const H = perf.h;
  const pos = [position[0] + q('perfx', 0), perf.y, position[2] + q('perfz', 0)];
  const [aspect, setAspect] = useState(0.5625);
  const [tex, setTex] = useState(null);
  const videoRef = useRef(null);
  // This video IS the concert's music: the HUD Music button (musicOn) mutes /
  // unmutes it, and the district station ducks while it's up so they don't clash.
  const musicOn = useWorldStore((s) => s.musicOn);
  // Duck the world sound bed while the concert plays; on exit, restore the world
  // audio to the CURRENT toggle state (so a "music-off" in the concert doesn't
  // blast the station when you step back to the street).
  useEffect(() => {
    audioEngine.duck(true);
    return () => { audioEngine.setMusicOn(useWorldStore.getState().musicOn).catch(() => {}); };
  }, []);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !musicOn;
    if (musicOn) v.play().catch(() => {}); // unmuting is a gesture-gated action; keep it rolling
  }, [musicOn, tex]);

  useEffect(() => {
    if (!url || VIDEO_OFF) return undefined;   // stills test: the screen stays dark
    const v = document.createElement('video');
    videoRef.current = v;
    v.crossOrigin = 'anonymous'; v.src = proxiedMedia(url);
    v.loop = true; v.muted = true; v.defaultMuted = true; v.playsInline = true; v.preload = 'auto';
    v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
    // Attach hidden to the DOM — some browsers won't decode/autoplay a detached
    // <video>, which left the VideoTexture black (and the luma key ate it → NO VIDEO).
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // Autoplay may still be blocked pre-gesture — retry on the next interaction.
    const onGesture = () => tryPlay();
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    const onMeta = () => { if (v.videoWidth) setAspect(v.videoWidth / v.videoHeight); };
    v.addEventListener('loadedmetadata', onMeta);
    const t = new THREE.VideoTexture(v); t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      v.removeEventListener('loadedmetadata', onMeta);
      v.pause(); v.removeAttribute('src'); v.load();
      try { document.body.removeChild(v); } catch { /* already gone */ }
      videoRef.current = null;
      t.dispose();
    };
  }, [url]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    uniforms: { map: { value: null }, keyLo: { value: q('keylo', 0.06) }, keyHi: { value: q('keyhi', 0.22) } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `
      uniform sampler2D map; uniform float keyLo; uniform float keyHi; varying vec2 vUv;
      void main() {
        vec4 c = texture2D(map, vUv);
        float luma = dot(c.rgb, vec3(0.299, 0.587, 0.114));
        float a = smoothstep(keyLo, keyHi, luma); // black bg → transparent, figure → opaque
        if (a < 0.02) discard;
        gl_FragColor = vec4(c.rgb, a);
      }`,
  }), []);
  useEffect(() => { if (tex) material.uniforms.map.value = tex; }, [tex, material]);
  useEffect(() => () => material.dispose(), [material]);

  if (!tex) return null;
  return (
    <mesh position={pos} material={material}>
      <planeGeometry args={[H * aspect, H]} />
    </mesh>
  );
}

// Chooser: ?perfvideo → keyed video, else ?perfimg → alpha still.
function Performer({ img, video, ...props }) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const qVideo = params?.get('perfvideo');
  const qImg = params?.get('perfimg');
  // ?perfvideo forces video, ?perfimg forces image; otherwise a configured video
  // default (CONCERT_HALL_1_PERFORMER) wins over an image default.
  const useVideo = !!qVideo || (!qImg && !!video);
  return useVideo
    ? <PerformerVideo {...props} video={video} />
    : <PerformerImage {...props} img={img} />;
}

function ConcertHall({ beatPhase, energy }) {
  const { splat, splatReady } = useSplatRoom('concert-hall');

  // Derive the room from the stage footprint so it always ENCLOSES the stage and
  // the stage sits AGAINST the back wall (not floating centred). Auto-tracks
  // ?stagew / ?stagez, so tuning the stage resizes the hall to match.
  const stageW = qp('stagew', 48), stageZ = qp('stagez', -22);
  const s = stageW / STAGE_BOX.w;
  const stageBackZ = STAGE_BOX.zMin * s + stageZ;   // stage's back edge
  const stageFrontZ = STAGE_BOX.zMax * s + stageZ;  // stage's front edge
  const halfW = stageW / 2;
  // Where the performer stands + the lights aim: the stage CENTRE (the deck,
  // with the 3D troupe) — the front edge of the GLB is an apron that reads as
  // "in front of" the stage. Tracks the stage; nudge with ?perfz.
  const stageCenterZ = ((STAGE_BOX.zMin + STAGE_BOX.zMax) / 2) * s + stageZ;
  const performZ = stageCenterZ;
  const room = useMemo(() => {
    const backZ = stageBackZ - 0.5;                 // wall just behind the stage
    const frontZ = 22;                              // behind the audience (spawn z≈7)
    const sideX = halfW + 5;                         // walls just outside the stage
    const h = Math.max(STAGE_BOX.h * s + 6, 16);
    return { backZ, frontZ, sideX, h, midZ: (backZ + frontZ) / 2, w: sideX * 2, d: frontZ - backZ };
  }, [stageBackZ, halfW, s]);

  // Knot sculpture: centred in the room and scaled LARGER than it on every axis,
  // so the viewer stands inside the knot and never sees its extremities. The fit
  // is derived from the room box (which itself tracks ?stagew), so tuning the
  // stage re-sizes the sculpture with the hall. Centred at half the wall height:
  // strands sweep from under the floor to over the ceiling, and the walls crop
  // everything but the arcs crossing the room.
  const knotFill = qp('knotfill', 1.6);
  const knotPos = [qp('knotx', 0), qp('knoty', room.h * 0.5), qp('knotz', room.midZ)];

  // Bounds: keep the viewer inside the walls, and BLOCK OFF THE STAGE for
  // non-admins — they stop just in front of the stage's front edge; admins (and
  // later "performers" cleared for a live show) may walk onto the deck.
  // CameraBounds (PlanetScene) clamps the camera to these each frame.
  const isAdmin = useMemo(() => authUser().isAdmin, []);
  const setRoomBounds = useWorldStore((st) => st.setRoomBounds);
  const setBoundsEnabled = useWorldStore((st) => st.setBoundsEnabled);
  useEffect(() => {
    const minZ = isAdmin ? room.backZ + 1 : stageFrontZ + 1.5; // non-admin can't step onto the stage
    setRoomBounds({ min: [-(room.sideX - 1), 0.4, minZ], max: [room.sideX - 1, room.h - 1.5, room.frontZ - 1] });
    setBoundsEnabled(true);
    return () => setRoomBounds(null);
  }, [isAdmin, room.backZ, room.sideX, room.h, room.frontZ, stageFrontZ, setRoomBounds, setBoundsEnabled]);
  return (
    <group>
      {splat}

      {/* The baked virtual stage — the real stage now, so it renders whether or
          not a splat is present. Tune with ?stagew / ?stagez / ?stagerot. */}
      <StageModel />

      {/* Scale-check lineup: the roster avatars at real height, so the stage can
          be sized against them. Tune with ?avh / ?avz / ?avgap / ?avy / ?avrot. */}
      <ConcertHallAvatars />

      {!splatReady && (
        <>
          {/* Dark hall shell, DERIVED from the stage footprint: encloses it and
              seats it against the back wall. Floor sits at y=−0.08 — just BELOW
              the stage's y=0 base — so the two coplanar planes don't z-fight
              (the flashing floor). Placeholder until a real concert-hall model. */}
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, room.midZ]}>
            <planeGeometry args={[room.w, room.d]} />
            <meshStandardMaterial color="#0e0e14" roughness={1} />
          </mesh>
          {[
            { p: [0, room.h / 2, room.backZ], r: [0, 0, 0], s: [room.w, room.h] },              // back — stage against it
            { p: [-room.sideX, room.h / 2, room.midZ], r: [0, Math.PI / 2, 0], s: [room.d, room.h] }, // left
            { p: [room.sideX, room.h / 2, room.midZ], r: [0, -Math.PI / 2, 0], s: [room.d, room.h] }, // right
            { p: [0, room.h / 2, room.frontZ], r: [0, Math.PI, 0], s: [room.w, room.h] }         // front — behind audience
          ].map((w, i) => (
            <mesh key={i} position={w.p} rotation={w.r}>
              <planeGeometry args={w.s} />
              <meshStandardMaterial color="#14141c" roughness={1} />
            </mesh>
          ))}
        </>
      )}

      {/* The performer — a transparent-PNG pose cutout composited on the stage
          (?perfimg=<url>; falls back to a hologram until set). On the deck, in
          front of the screen; follows the stage via performZ. */}
      <Suspense fallback={null}>
        <Performer
          position={[0, 1.74, performZ]}
          height={2.65}
          img={WORLD_PERFORMER.img || ROOMS['concert-hall'].performer?.img}
          video={WORLD_PERFORMER.video}
        />
      </Suspense>
      <pointLight position={[0, 3, performZ]} color="#7dd3ff" intensity={16} distance={16} />

      {/* The particle sculpture — centred on the hall and bigger than it, so its
          orbs circle THROUGH the room around you, breathing on the beat.
          Off with ?knot=0; re-size with ?knotfill / ?knotspan / ?knotsize. */}
      {qp('knot', 1) !== 0 && (
        <KnotSculpture
          position={knotPos}
          fitW={room.w}
          fitH={room.h}
          fitD={room.d}
          fill={knotFill}
          energy={energy}
        />
      )}

      {/* Stage wash + reactive particles — the "show" layer, over the stage. */}
      <StageParticles energy={energy} accent="#7dd3ff" centerZ={performZ} spreadX={Math.min(halfW * 1.6, 30)} spreadZ={16} />
      <AimedSpot position={[-6, 8, performZ + 6]} aim={[0, 1.4, performZ]} angle={0.5} penumbra={0.6} color="#ff5e78" intensity={30} distance={24} />
      <AimedSpot position={[6, 8, performZ + 6]} aim={[0, 1.4, performZ]} angle={0.5} penumbra={0.6} color="#ffb25e" intensity={30} distance={24} />

      {/* Other people now render globally via <Peers/> at their real positions. */}

      <ambientLight intensity={0.12} color="#3a3a55" />
    </group>
  );
}

// ── The Cinema ──────────────────────────────────────────────────────────────
// A black-box theater with one big flat 16:9 IMAX screen on the back wall,
// playing the /listen music videos back-to-back WITH sound (the HUD Music button
// mutes/unmutes; the district station ducks underneath). Room geometry:
const THEATER = {
  sideX: 13, backZ: -15, frontZ: 13, h: 15,
  screen: { w: 18, cy: 6.5, z: -14.8 }, // 18×10.125 (16:9), raised, on back wall
};

// TheaterScreen — fetches the music-video playlist, plays each clip in turn on a
// VideoTexture, and letterboxes the actual video aspect inside the fixed 16:9
// IMAX frame. Signed GCS URLs expire ~60min, so we re-fetch the playlist each
// time we loop back to the top (a full 12-clip loop is well under that).
function TheaterScreen() {
  const { w, cy, z } = THEATER.screen;
  const frameH = (w * 9) / 16;
  const musicOn = useWorldStore((s) => s.musicOn);
  const [videos, setVideos] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [tex, setTex] = useState(null);
  const [vidAspect, setVidAspect] = useState(16 / 9);
  const [label, setLabel] = useState('');
  const videoRef = useRef(null);
  const lenRef = useRef(0);

  const fetchPlaylist = () =>
    fetch(`${API_BASE}/api/music-videos/discover?limit=12`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { const v = d?.videos?.filter((x) => x.gcs_url) || []; if (v.length) { lenRef.current = v.length; setVideos(v); } })
      .catch(() => {});

  useEffect(() => { fetchPlaylist(); }, []);
  useEffect(() => { lenRef.current = videos.length; }, [videos]);

  // On leaving the cinema, clear the movie foreground AND restore the base to the
  // CURRENT Music toggle. Without the setMusicOn, clearForeground→resumeBase would
  // replay the station regardless of `musicOn` — desyncing the mute button (you
  // muted in the cinema, but the street came back playing). setMusicOn is
  // authoritative; the playBroadcast guard cancels resumeBase's stale play.
  useEffect(() => () => {
    audioEngine.clearForeground();
    audioEngine.setMusicOn(useWorldStore.getState().musicOn).catch(() => {});
  }, []);

  // Build the <video> for the current clip; on end, advance (wrap → re-fetch for
  // fresh signed URLs). Full teardown/rebuild per clip keeps it simple.
  useEffect(() => {
    if (!videos.length) return undefined;
    const cur = videos[cursor % videos.length];
    setLabel(`${cur.track_title || cur.title || ''}${cur.avatar_name ? '  ·  ' + cur.avatar_name : ''}`.trim());
    const v = document.createElement('video');
    videoRef.current = v;
    v.crossOrigin = 'anonymous';
    v.src = proxiedMedia(cur.gcs_url);
    v.loop = false;
    v.muted = !useWorldStore.getState().musicOn;
    v.playsInline = true; v.preload = 'auto';
    v.setAttribute('playsinline', '');
    if (v.muted) v.setAttribute('muted', '');
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    // This clip now owns the venue's sound bed (station goes silent under it);
    // audibility follows the Music toggle via setForegroundMuted below.
    audioEngine.setForeground(v);

    const advance = () => setCursor((c) => {
      const nc = c + 1;
      if (nc >= (lenRef.current || 1)) { fetchPlaylist(); return 0; }
      return nc;
    });
    const onMeta = () => { if (v.videoWidth) setVidAspect(v.videoWidth / v.videoHeight); };
    v.addEventListener('ended', advance);
    v.addEventListener('error', advance);
    v.addEventListener('loadedmetadata', onMeta);
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    const onGesture = () => tryPlay();
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);

    const t = new THREE.VideoTexture(v); t.colorSpace = THREE.SRGBColorSpace;
    setTex(t);
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      v.removeEventListener('ended', advance);
      v.removeEventListener('error', advance);
      v.removeEventListener('loadedmetadata', onMeta);
      v.pause(); v.removeAttribute('src'); v.load();
      try { document.body.removeChild(v); } catch { /* already gone */ }
      videoRef.current = null;
      t.dispose();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, cursor]);

  // Music button drives the movie's audibility through the mixer (which also
  // keeps the station silenced underneath and yields to hover-news).
  useEffect(() => {
    audioEngine.setForegroundMuted(!musicOn);
  }, [musicOn, tex]);

  // "Contain" the real video aspect inside the fixed 16:9 frame (letterbox).
  const frameAspect = 16 / 9;
  const inW = vidAspect >= frameAspect ? w : frameH * vidAspect;
  const inH = vidAspect >= frameAspect ? w / vidAspect : frameH;

  return (
    <group position={[0, cy, z]}>
      {/* Frame bezel */}
      <mesh position={[0, 0, -0.06]}>
        <planeGeometry args={[w + 0.8, frameH + 0.8]} />
        <meshStandardMaterial color="#050507" roughness={1} />
      </mesh>
      {/* Black screen backing (fills the frame; letterbox bars show through) */}
      <mesh position={[0, 0, -0.03]}>
        <planeGeometry args={[w, frameH]} />
        <meshBasicMaterial color="#000000" toneMapped={false} />
      </mesh>
      {/* The video, sized to its true aspect within the frame */}
      {tex && (
        <mesh>
          <planeGeometry args={[inW, inH]} />
          <meshBasicMaterial map={tex} toneMapped={false} />
        </mesh>
      )}
      {/* Now-playing caption under the screen */}
      {label && (
        <Text position={[0, -(frameH / 2) - 0.85, 0]} fontSize={0.62} color="#c8a2ff" anchorX="center" anchorY="middle" maxWidth={w} outlineWidth={0.006} outlineColor="#000">
          {label}
        </Text>
      )}
      {/* Soft screen glow spilling into the house */}
      <pointLight position={[0, 0, 4]} color="#9a7bff" intensity={10} distance={26} />
    </group>
  );
}

// A block of cinema seats — cheap silhouettes so the house reads as a theater
// without a model. Rows recede in +z (toward the spawn); the screen is on the
// back wall at −z, so the seats FACE −z (backrest on the +z side). Seated flat
// on the floor (no float).
function TheaterSeats() {
  const rows = 6, perRow = 11, gapX = 1.7, gapZ = 1.9, z0 = -2;
  const seats = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < perRow; c++) {
      const x = (c - (perRow - 1) / 2) * gapX;
      const z = z0 + r * gapZ;
      seats.push([x, 0, z]);
    }
  }
  return (
    <group>
      {seats.map((p, i) => (
        <group key={i} position={p}>
          {/* cushion — bottom rests on the floor (~y 0.03) */}
          <mesh castShadow position={[0, 0.28, 0]}><boxGeometry args={[1.0, 0.5, 0.9]} /><meshStandardMaterial color="#241826" roughness={0.9} /></mesh>
          {/* backrest — on the +z side so the occupant faces −z toward the screen */}
          <mesh position={[0, 0.72, 0.42]}><boxGeometry args={[1.0, 0.8, 0.16]} /><meshStandardMaterial color="#2e1f30" roughness={0.95} /></mesh>
        </group>
      ))}
    </group>
  );
}

function Theater() {
  const { sideX, backZ, frontZ, h } = THEATER;
  const midZ = (backZ + frontZ) / 2, w = sideX * 2, d = frontZ - backZ;

  // Bounds: inside the walls, and kept ~4m off the screen wall so the viewer
  // can't clip through the screen.
  const setRoomBounds = useWorldStore((st) => st.setRoomBounds);
  const setBoundsEnabled = useWorldStore((st) => st.setBoundsEnabled);
  useEffect(() => {
    setRoomBounds({ min: [-(sideX - 1), 0.4, backZ + 4], max: [sideX - 1, h - 1.5, frontZ - 1] });
    setBoundsEnabled(true);
    return () => setRoomBounds(null);
  }, [sideX, backZ, frontZ, h, setRoomBounds, setBoundsEnabled]);

  return (
    <group>
      {/* Black-box shell */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, midZ]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#0a0810" roughness={1} />
      </mesh>
      {[
        { p: [0, h / 2, backZ], r: [0, 0, 0], s: [w, h] },
        { p: [-sideX, h / 2, midZ], r: [0, Math.PI / 2, 0], s: [d, h] },
        { p: [sideX, h / 2, midZ], r: [0, -Math.PI / 2, 0], s: [d, h] },
        { p: [0, h / 2, frontZ], r: [0, Math.PI, 0], s: [w, h] },
      ].map((wall, i) => (
        <mesh key={i} position={wall.p} rotation={wall.r}>
          <planeGeometry args={wall.s} />
          <meshStandardMaterial color="#120e18" roughness={1} />
        </mesh>
      ))}
      {/* Ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, h, midZ]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color="#0a0810" roughness={1} />
      </mesh>

      <TheaterScreen />
      <TheaterSeats />

      {/* Ambient News-podcast flatscreens down the side walls — the same
          visual_timeline slideshow the /listen podcast plays, synced across every
          TV by the shared world clock (services/newsPodcast). Muted background. */}
      {[-1, 1].map((sx) => [1, 8].map((zz, i) => (
        <NewsTv
          key={`news-${sx}-${i}`}
          position={[sx * (sideX - 0.12), 3.4, zz]}
          rotationY={sx < 0 ? Math.PI / 2 : -Math.PI / 2}
          width={2.8}
        />
      )))}

      {/* Dim house lights — sconces down the side walls */}
      {[-1, 1].map((sx) => [-8, 0, 8].map((zz, i) => (
        <pointLight key={`${sx}-${i}`} position={[sx * (sideX - 0.6), 3.4, zz]} color="#7a5cff" intensity={2.2} distance={9} />
      )))}
      <ambientLight intensity={0.08} color="#3a2a55" />
    </group>
  );
}

export default function Interior({ beatPhase = 0, energy = 0.5 }) {
  const interior = useWorldStore((s) => s.interior);
  const exitInterior = useWorldStore((s) => s.exitInterior);
  const isHall = interior === 'concert-hall';

  return (
    <group>
      {/* Numbered tags on everything placed in this room — the in-scene half
          of ⚙ → Room placeables. Renders nothing unless that tool is on. */}
      <PlaceableBadges />
      {interior === 'studio' && <Studio beatPhase={beatPhase} energy={energy} />}
      {interior === 'listening-room' && <ListeningRoom beatPhase={beatPhase} energy={energy} />}
      {interior === 'record-store' && <RecordStore />}
      {interior === 'room-studio' && <ScannedRoom />}
      {/* Baked moment scenes (rooms as data — App registers the ROOMS entry
          from the ?enter=moment-<postId> deep link). */}
      {typeof interior === 'string' && interior.startsWith('moment-') && <ScannedRoom roomId={interior} />}
      {isHall && <ConcertHall beatPhase={beatPhase} energy={energy} />}
      {interior === 'theater' && <Theater />}
      {/* Any room that declares a `model` is a GLB venue — one generic component
          for all of them, so adding an experience area is a rooms.js entry
          rather than another branch here. Suspense is per-venue: a big interior
          still loading must not blank the exit chip you need to leave by. */}
      {ROOMS[interior]?.model && (
        <Suspense fallback={null}><MeshInterior roomId={interior} /></Suspense>
      )}
      {/* Bodies standing in the room — SPLAT ROOMS ONLY. A GLB venue renders its
          own, inside MeshInterior.
          Not a tidy-up: this component sits at the WORLD ORIGIN, which is where
          a splat room is built, but a SITED venue stands at its street address.
          Residents are saved in room-local coordinates, so drawing them here put
          a body placed in the shop seventy units away in the middle of the city —
          present, correct, and nowhere you would ever look for it. MeshInterior
          renders them inside its own positioned group, where those coordinates
          mean what they say. */}
      {!ROOMS[interior]?.model && <InteriorResidents roomId={interior} />}
      {/* Banter belongs to the ROOM, not to the renderer: a splat room with a
          cast talks exactly like a mesh room with one. */}
      {!ROOMS[interior]?.model && <EnsembleDirector roomId={interior} />}
      {/* Listeners in the chairs — and the same argument as the two above.
          THE LISTENING ROOM IS A SPLAT (Khari, 2026-08-04: "we literally have a
          'listening room' on the map"), so mounting the seated bodies only on
          the mesh path meant the feature could never appear in the room it is
          named after. Seats there are placed rows rather than mesh affordances;
          `seatsFor` resolves whichever the room actually has. */}
      {!ROOMS[interior]?.model && <ListeningSeats />}
      {/* Exit chip — conventional UI where wayfinding demands it (Design Law 1 amendment) */}
      <Html position={[0, isHall || interior === 'theater' ? 6 : 2.5, 0]} center>
        <button
          onClick={exitInterior}
          style={{
            background: 'rgba(0,0,0,0.55)', color: '#ffd27d', border: '1px solid #ffb25e',
            padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 14
          }}
        >
          ← Back to the street
        </button>
      </Html>
    </group>
  );
}
